import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Flame, MessageCircle, Trash2 } from "lucide-react";

export const SalesCard = () => {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [service, setService] = useState("");
  const [pct, setPct] = useState("10");
  const [days, setDays] = useState("7");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/professional/sales").then(({ data }) => setSales(data.sales)).catch(() => {});
  useEffect(() => { load(); }, []);

  const start = async () => {
    if (!service) return toast.error("Pick a service to put on sale");
    setBusy(true);
    try {
      const { data } = await api.post("/professional/sales", { service, pct: parseFloat(pct), days: parseInt(days) });
      toast.success(data.blast_count > 0
        ? `🔥 Sale live — we texted ${data.blast_count} of your past client${data.blast_count === 1 ? "" : "s"}!`
        : `🔥 Sale live: ${pct}% OFF ${service} — now share it!`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const end = async (id) => {
    try { await api.delete(`/professional/sales/${id}`); toast.success("Sale ended"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const shareText = (s) =>
    `🔥 SALE ALERT: ${s.pct.toFixed(0)}% OFF ${s.service} with ${user.name} on Care-4U.net — only until ${new Date(s.ends_at).toLocaleDateString()}! Book here: ${window.location.origin}/provider/${user.id}`;

  return (
    <div className="card-metal p-6" data-testid="sales-card">
      <h2 className="font-heading font-black text-lg flex items-center gap-2">
        <Flame className="w-5 h-5 text-[#9b111e]" /> Run a Sale
      </h2>
      <p className="text-sm text-slate-600 mt-1">Discount any of your services for a limited time, then blast it to your socials — sale prices apply automatically at booking.</p>
      <div className="flex flex-wrap gap-2 mt-3 items-center">
        <Select value={service} onValueChange={setService}>
          <SelectTrigger className="bg-white max-w-[220px]" data-testid="sale-service-select"><SelectValue placeholder="Service" /></SelectTrigger>
          <SelectContent>
            {(user.services || []).map((s) => <SelectItem key={s.name} value={s.name}>{s.name} — ${s.price}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="number" min="5" max="50" value={pct} onChange={(e) => setPct(e.target.value)} className="bg-white w-20" data-testid="sale-pct-input" />
        <span className="text-sm text-slate-500">% off for</span>
        <Input type="number" min="1" max="30" value={days} onChange={(e) => setDays(e.target.value)} className="bg-white w-20" data-testid="sale-days-input" />
        <span className="text-sm text-slate-500">days</span>
        <button onClick={start} disabled={busy} className="btn-red-3d !py-2 !px-4" data-testid="start-sale-btn">Start Sale 🔥</button>
      </div>
      {sales.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="active-sales-list">
          {sales.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border-2 border-[#9b111e] bg-red-50/60 p-3" data-testid={`sale-${s.id}`}>
              <p className="text-sm font-bold text-slate-800">
                🔥 {s.pct.toFixed(0)}% OFF {s.service}
                <span className="font-normal text-slate-500"> · ends {new Date(s.ends_at).toLocaleDateString()}</span>
                <span className={`block text-xs mt-0.5 ${s.bookings > 0 ? "text-green-700 font-bold" : "text-slate-400 font-normal"}`} data-testid={`sale-stats-${s.id}`}>
                  {s.bookings > 0 ? `🎉 ${s.bookings} booking${s.bookings === 1 ? "" : "s"} from this sale · $${(s.revenue || 0).toFixed(2)} earned` : "No bookings from this sale yet — share it below!"}
                </span>
              </p>
              <div className="flex flex-wrap gap-1.5 items-center">
                <a href={`sms:?&body=${encodeURIComponent(shareText(s))}`} data-testid={`share-sale-sms-${s.id}`}
                  className="inline-flex items-center gap-1 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-full px-3 py-1.5">
                  <MessageCircle className="w-3.5 h-3.5" /> Text
                </a>
                <a href={`https://wa.me/?text=${encodeURIComponent(shareText(s))}`} target="_blank" rel="noreferrer" data-testid={`share-sale-whatsapp-${s.id}`}
                  className="text-xs font-bold text-white bg-[#25D366] hover:bg-[#1ebe5b] rounded-full px-3 py-1.5">WhatsApp</a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/provider/${user.id}`)}&quote=${encodeURIComponent(shareText(s))}`}
                  target="_blank" rel="noreferrer" data-testid={`share-sale-facebook-${s.id}`}
                  className="text-xs font-bold text-white bg-[#1877F2] hover:bg-[#0f6ae0] rounded-full px-3 py-1.5">Facebook</a>
                <button onClick={() => end(s.id)} title="End sale" data-testid={`end-sale-${s.id}`}
                  className="p-1.5 rounded-full text-red-600 hover:bg-red-100"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SalesCard;
